import { useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  LightspeedBusiness,
  LightspeedStore,
  getConnectedLightspeedStores
} from '../types'
import { PARTNER_SLUGS } from '../../../constants'
import { useIntegration } from '../../../hooks/useIntegration'

const LIGHTSPEED_PATH = `/integrations/${PARTNER_SLUGS.LIGHTSPEED}`

export const useLocationPageData = () => {
  const navigate = useNavigate()
  const { storeId: storeIdParam } = useParams()
  const [searchParams] = useSearchParams()
  const isNewConnection = storeIdParam === 'connect'

  const { lightspeedBusinesses: businesses, lightspeedStores } =
    useIntegration()

  const connectedLocations = useMemo(
    () => getConnectedLightspeedStores(lightspeedStores),
    [lightspeedStores]
  )

  const selectedBusiness = useMemo<LightspeedBusiness | null>(() => {
    if (!businesses.length) return null

    if (isNewConnection) {
      const businessId = searchParams.get('businessId')
      return businesses.find((b) => String(b.id) === businessId) || null
    }

    const location = connectedLocations.find((l) => l.id === storeIdParam)
    if (location) {
      return (
        businesses.find(
          (b) =>
            String(b.id) ===
            String(location.store_integration_settings.lightspeed_business_id)
        ) || null
      )
    }

    return null
  }, [
    businesses,
    connectedLocations,
    storeIdParam,
    isNewConnection,
    searchParams
  ])

  const selectedLocation = useMemo<LightspeedStore | null>(() => {
    if (isNewConnection) return null
    return connectedLocations.find((l) => l.id === storeIdParam) || null
  }, [connectedLocations, storeIdParam, isNewConnection])

  useEffect(() => {
    if (!selectedBusiness) {
      navigate(LIGHTSPEED_PATH, { replace: true })
    }
  }, [selectedBusiness])

  return {
    selectedBusiness,
    selectedLocation,
    connectedLocations,
    isNewConnection
  }
}
